import { Text } from '@southfields-digital/mpxlive-components';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ActiveGraphics, { ActiveGraphicsHeader } from 'src/components/ActiveGraphics';
import { StatusDescription } from 'src/components/GoLive';
import { getMappedCanvases } from 'src/components/GoLive/utils';
import BundledGraphics from 'src/components/BundledGraphics';
import MatchControl from 'src/components/MatchControl';
import QuickControl from 'src/components/QuickControl/QuickControl';
import {
  PanelGroup,
  Panel,
  PanelResizeHandle as ResizeHandle,
} from 'src/components/ResizeablePanel';
import ToggleButtons from 'src/components/ToggleButtons/ToggleButtons';
import ViewWindow from 'src/components/ViewWindow/ViewWindow';
import { StateType } from 'src/redux/reducers';

import LayoutSimplified from './Simplified';

export default function LayoutDefault() {
  const { rundownId } = useParams<{ rundownId: string }>();
  const { data, outputs } = useSelector((state: StateType) => state.rundown.detail);
  const { bundledPlaylistGraphics } = useSelector(
    (state: StateType) => state.bundledPlaylistGraphic
  );

  const [selectedCanvas, setSelectedCanvas] = useState<RundownTemplateCanvas | undefined>();
  const [showMatchControl, setShowMatchControl] = useState<boolean>(false);
  const [showBundledGraphics, setShowBundledGraphics] = useState<boolean>(false);

  useEffect(() => {
    if (data?.isLive && !selectedCanvas) {
      const activeCanvas = data.canvases.find((canvas) => canvas.output && canvas.graphics.length);
      if (activeCanvas) {
        setSelectedCanvas(activeCanvas);
      }
    }
  }, [data]);

  if (!data || !rundownId) return null;

  return (
    <LayoutSimplified>
      <ResizeHandle />

      <Panel defaultSize={25} maxSize={50} id="third-column" order={3}>
        <PanelGroup direction="vertical">
          <StatusDescription
            rundown={data}
            mappedCanvases={getMappedCanvases(data.canvases, outputs)}
            isLive={data.isLive}
            className="border-b-[4px] border-[#151621]"
          />
          {data.isLive && (
            <>
              <Panel
                id="canvas"
                order={1}
                defaultSize={37}
                header={
                  <ToggleButtons
                    items={data.canvases.filter((canvas) => canvas.graphics.length)}
                    selectedItem={selectedCanvas}
                    onClickItem={(canvas) => setSelectedCanvas(canvas)}
                  />
                }
              >
                {selectedCanvas?.output?.liveUrl ? (
                  <ViewWindow name="PGM" src={selectedCanvas.output.liveUrl} liveMode />
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <Text as="p" className="!text-sm !text-gray-300 uppercase p-2">
                      Configure an output for this canvas
                    </Text>
                  </div>
                )}
              </Panel>
              <ResizeHandle />
            </>
          )}
          <Panel
            id="quickControls"
            order={2}
            defaultSize={data.isLive ? 32.25 : 35}
            header={
              <ToggleButtons
                items={[
                  { id: 'quickControls', name: 'Quick Controls' },
                  {
                    id: 'matchControls',
                    name: 'Match Control',
                    visible: Boolean(data.matchControl),
                  },
                  {
                    id: 'bundledGraphics',
                    name: 'Bundle Controls',
                    visible: Boolean(bundledPlaylistGraphics?.length),
                  },
                ]}
                selectedItem={
                  showMatchControl
                    ? { id: 'matchControls', name: 'Match Control' }
                    : showBundledGraphics
                      ? { id: 'bundledGraphics', name: 'Bundle Controls' }
                      : { id: 'quickControls', name: 'Quick Controls' }
                }
                onClickItem={(item) => {
                  setShowMatchControl(item.id === 'matchControls');
                  setShowBundledGraphics(item.id === 'bundledGraphics');
                }}
              />
            }
          >
            {showMatchControl ? (
              <MatchControl rundownId={rundownId} />
            ) : showBundledGraphics ? (
              <BundledGraphics />
            ) : (
              <QuickControl />
            )}
          </Panel>

          <ResizeHandle />

          <Panel
            header={<ActiveGraphicsHeader />}
            id="activeGraphics"
            order={3}
            overflow={false}
          >
            <ActiveGraphics />
          </Panel>
        </PanelGroup>
      </Panel>
    </LayoutSimplified>
  );
}
